import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { RestaurantProvider } from './contexts/RestaurantContext';
import { NotificationProvider } from './contexts/NotificationContext';
import ProtectedRoute from './components/auth/ProtectedRoute';
import Layout from './components/layout/Layout';
import LoginForm from './components/auth/LoginForm';
import Dashboard from './components/dashboard/Dashboard';
import ReservationList from './components/reservations/ReservationList';
import ReservationForm from './components/reservations/ReservationForm';
import TodayReservations from './components/reservations/TodayReservations';
import RestaurantSettings from './components/settings/RestaurantSettings';
import AvailabilityClosures from './components/settings/AvailabilityClosures';
import StatsPage from './components/stats/StatsPage';
import { forceServiceWorkerUpdate } from './utils/serviceWorkerUtils';
import './index.css';

function App() {
  useEffect(() => {
    // Register service worker for PWA functionality with update checking
    if ('serviceWorker' in navigator) {
      const registerServiceWorker = async () => {
        try {
          const registration = await navigator.serviceWorker.register('/sw.js', {
            updateViaCache: 'none' // Always check for updates, don't use cache
          });
          
          console.log('SW registered: ', registration);
          
          // Check for updates immediately
          await registration.update();
          
          // Check for updates every 60 seconds
          setInterval(() => {
            registration.update();
          }, 60000);
          
          // Listen for updates
          registration.addEventListener('updatefound', () => {
            const newWorker = registration.installing;
            if (newWorker) {
              newWorker.addEventListener('statechange', () => {
                if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                  // New service worker is ready, reload to activate it
                  console.log('New service worker available, reloading...');
                  window.location.reload();
                }
              });
            }
          });
          
          // Force update function is already exposed via serviceWorkerUtils
          // But we can also use the registration directly
          (window as any).forceServiceWorkerUpdate = () => forceServiceWorkerUpdate();
          
        } catch (registrationError) {
          console.log('SW registration failed: ', registrationError);
        }
      };
      
      // Register immediately, don't wait for load event
      registerServiceWorker();
    }

    // Add PWA install prompt handling
    let deferredPrompt: any = null;
    const handleBeforeInstallPrompt = (e: any) => {
      deferredPrompt = e;
      e.preventDefault();
      
      // Show install button or banner if desired
      console.log('PWA install prompt available', deferredPrompt);
    };

    const handleAppInstalled = () => {
      console.log('PWA was installed');
      deferredPrompt = null;
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    // Handle online/offline status
    const updateOnlineStatus = () => {
      const statusElement = document.getElementById('network-status');
      if (statusElement) {
        if (navigator.onLine) {
          statusElement.classList.add('hidden');
        } else {
          statusElement.classList.remove('hidden');
        }
      }
    };

    window.addEventListener('online', updateOnlineStatus);
    window.addEventListener('offline', updateOnlineStatus);
    updateOnlineStatus();

    return () => {
      window.removeEventListener('online', updateOnlineStatus);
      window.removeEventListener('offline', updateOnlineStatus);
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  return (
    <div className="App">
      {/* Offline indicator */}
      <div
        id="network-status"
        className="hidden fixed top-0 left-0 right-0 bg-red-500 text-white text-center py-2 text-sm z-50"
      >
        Está offline. Algumas funcionalidades podem estar limitadas.
      </div>

      <AuthProvider>
        <NotificationProvider>
          <Router>
            <Routes>
              {/* Public routes */}
              <Route path="/login" element={<LoginForm />} />
              
              {/* Protected routes */}
              <Route
                path="/*"
                element={
                  <ProtectedRoute>
                    <RestaurantProvider>
                      <Layout />
                    </RestaurantProvider>
                  </ProtectedRoute>
                }
              >
              <Route index element={<Navigate to="/dashboard" replace />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="reservations" element={<ReservationList />} />
              <Route path="reservations/new" element={<ReservationForm />} />
              <Route path="reservations/today" element={<TodayReservations />} />
              <Route path="stats" element={<StatsPage />} />
              <Route
                path="availability-closures"
                element={
                  <ProtectedRoute requiredPermissions={['manager']}>
                    <AvailabilityClosures />
                  </ProtectedRoute>
                }
              />
              <Route
                path="settings"
                element={
                  <ProtectedRoute requiredPermissions={['manager']}>
                    <RestaurantSettings />
                  </ProtectedRoute>
                }
              />
              
              {/* Redirect unknown routes to dashboard */}
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Route>
          </Routes>
        </Router>
        </NotificationProvider>
      </AuthProvider>
    </div>
  );
}

export default App;